import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { History, User, Calendar, ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

const FIELD_LABELS = {
  name: "Okul Adı", short_intro: "Kısa Tanıtım", description: "Açıklama",
  long_description: "Uzun Açıklama", province: "İl", district: "İlçe",
  address: "Adres", phone: "Telefon", email: "E-posta", website: "Website",
  cover_url: "Kapak Görseli", logo_url: "Logo", brochure_url: "Broşür",
  gallery: "Galeri", education_levels: "Eğitim Kademeleri", facilities: "Olanaklar",
  tuition_min: "Ücret Alt", boarding: "Yatılı", foreign_language: "Yabancı Dil",
  featured: "Öne Çıkar", is_published: "Yayında", status: "Durum",
};

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "Evet" : "Hayır";
  return String(v);
}

export default function SchoolAuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!user?.school_id) return;
    api.get(`${V1}/schools/${user.school_id}/audit-log?limit=100`)
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, [user?.school_id]);

  return (
    <div className="space-y-6" data-testid="school-audit-log-page">
      <div>
        <div className="overline text-accent">Şeffaflık</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <History className="h-7 w-7 text-primary" /> Değişiklik Kaydı
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Okulunuzun bilgilerinde yapılmış her düzenlemenin şeffaf kaydı — kim, ne zaman, hangi alanı nasıl değiştirdi.
        </p>
      </div>

      {items.length === 0 && (
        <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-success" />
          Henüz kayıt yok — okul profilinize henüz düzenleme yapılmamış.
        </div>
      )}

      <div className="space-y-2">
        {items.map((log) => {
          const isOpen = expanded === log.id;
          return (
            <div key={log.id} className="rounded-xl border border-border bg-card" data-testid={`audit-row-${log.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : log.id)} className="w-full text-left p-4 flex items-start gap-3 hover:bg-muted/30">
                {isOpen ? <ChevronDown className="h-4 w-4 mt-1 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-1 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" /> {new Date(log.created_at).toLocaleString("tr-TR")}
                    <span>·</span>
                    <User className="h-3 w-3" /> {log.actor_name} ({log.actor_role})
                  </div>
                  <div className="mt-1 font-medium text-sm">
                    {log.field_count} alan güncellendi: {Object.keys(log.changes || {}).slice(0, 4).map((k) => FIELD_LABELS[k] || k).join(", ")}
                    {Object.keys(log.changes || {}).length > 4 && "…"}
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="p-4 pt-0 space-y-2 border-t border-border">
                  {Object.entries(log.changes || {}).map(([field, diff]) => (
                    <div key={field} className="p-3 rounded bg-muted/40" data-testid={`audit-change-${log.id}-${field}`}>
                      <div className="text-xs font-medium uppercase tracking-widest text-primary mb-2">{FIELD_LABELS[field] || field}</div>
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        <div className="p-2 rounded bg-destructive/5 border border-destructive/20">
                          <div className="text-[10px] uppercase tracking-widest text-destructive mb-1">Önceki</div>
                          <div className="break-words whitespace-pre-wrap text-xs">{fmt(diff.before)}</div>
                        </div>
                        <div className="p-2 rounded bg-success/5 border border-success/20">
                          <div className="text-[10px] uppercase tracking-widest text-success mb-1">Yeni</div>
                          <div className="break-words whitespace-pre-wrap text-xs">{fmt(diff.after)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
